import React, {useCallback, useState, useEffect} from "react";
import {Row, Descriptions, DatePicker, Select, InputNumber, Input, Form} from "antd";
import {CustomButton} from "../../../_defaultComponents/CustomButton/CustomButton";
import moment from "moment";
import {EditOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useFormik} from "formik";
import * as yup from "yup";
import {UserSelectInput} from "../../../_defaultComponents/UserSelectInput/UserSelectInput";
import {useStore, useFetch, useOptions} from "@vigo/hooks";
import companyService from "../../../../services/companyService";

const MODES = {
    VIEW: "view",
    EDIT: "edit"
};
const DEFAULT_MODE = MODES.VIEW;
export const SubCompanySalesTab = ({subcompanyId, subCompany}) => {
    const stores = useStore();
    const {t, i18n} = useTranslation(["subcompanyDetail", "courierTypes"]);
    const [mode, setMode] = useState(DEFAULT_MODE);
    const {optionLoading, options, findInOptions} = useOptions({groupName: "subCompanyDetail"});
    const [detailOptionsLoading, detailOptions] = useFetch({
        fetchFn: companyService.getSubcompanyDetailOptions,
        defaultDataValue: {},
        showLoading: false,
        initialCall: true,
        transform: response => {
            if (response && response.data) {
                return response.data;
            }
            return response;
        }
    });
    const handleOnChangeMode = () => {
        setMode(mode === MODES.VIEW ? MODES.EDIT : MODES.VIEW);
    };
    const [subcompanyDetail, setSubcompanyDetail] = useState({});
    const [recall, setRecall] = useState(false);

    const [_, subCompanyDetailData] = useFetch({
        fetchFn: stores.companyStore.getSubcompanyDetail.bind(null, {
            subcompanyId: subcompanyId
        }),

        defaultDataValue: {},
        showLoading: false,
        initialCall: true,
        condition: Boolean(subcompanyId),
        dependencies: [subcompanyId, recall],
        callbackFunc: data => {
            setSubcompanyDetail(data);
        }
    });

    const [, regionalDirectorie = {}] = useFetch({
        fetchFn: stores.userStore.getUser.bind(null, {
            userId: subCompanyDetailData?.regionalDirectorie
        }),
        showLoading: true,
        condition: Boolean(subCompanyDetailData?.regionalDirectorie),
        dependencies: [subCompanyDetailData?.regionalDirectorie],
        defaultDataValue: {}
    });

    //#region formik
    const requiredMessage = () => i18n.t("validation.required", {ns: "common"});
    const emailFormatMessage = () => i18n.t("validation.emailFormat", {ns: "common"});

    const formik = useFormik({
        initialValues: {
            contractDate: subcompanyDetail?.contractDate
                ? moment(subcompanyDetail.contractDate)
                : subCompany?.data?.createdAt
                ? moment(subCompany.data.createdAt)
                : undefined,
            openedDate: subcompanyDetail?.openedDate
                ? moment(subcompanyDetail.openedDate)
                : undefined,
            // workingPeriod: subCompany?.workingPeriod,
            statusCode: subcompanyDetail?.statusCode,
            sector: subcompanyDetail?.sector,
            priority: Boolean(subcompanyDetail?.priority),
            category: subcompanyDetail?.category,
            regionalDirectoriePlace: subcompanyDetail?.regionalDirectoriePlace,
            place: subcompanyDetail?.place,
            regionalDirectorie: subcompanyDetail?.regionalDirectorie,
            tp: subcompanyDetail?.tp,
            tpActual: subcompanyDetail?.tpActual,
            courierCount: subcompanyDetail?.courierCount,
            billingPeriod: subcompanyDetail?.billingPeriod,
            price: subcompanyDetail?.price,
            suretyAmountInclVAT: subcompanyDetail?.suretyAmountInclVAT,
            maturityDay: subcompanyDetail?.maturityDay,
            maxWorkingHour: subcompanyDetail?.maxWorkingHour,
            benefits: subcompanyDetail?.benefits || [],
            note: subcompanyDetail?.note,
            //priceAgreement: subcompanyDetail?.priceAgreement,
            mail: subcompanyDetail?.mail
        },
        validationSchema: yup.object().shape({
            contractDate: yup.date().nullable(),
            sector: yup.string().required(requiredMessage),
            priority: yup.boolean().required(requiredMessage),
            category: yup.string().required(requiredMessage),
            // regionalDirectoriePlace: Zone'dan otomatik doldurulur, edit edilemez
            place: yup.string().required(requiredMessage),
            // regionalDirectorie: Zone'dan otomatik doldurulur, edit edilemez
            tp: yup.number().required(requiredMessage),
            courierCount: yup.number().required(requiredMessage),
            billingPeriod: yup.string().required(requiredMessage),
            price: yup.number().required(requiredMessage),
            suretyAmountInclVAT: yup.number().nullable(),
            maturityDay: yup.string().required(requiredMessage),
            maxWorkingHour: yup
                .number()
                .nullable()
                .min(0, "Min değer 0 olmalıdır")
                .max(24, "Max değer 24 olmalıdır"),
            benefits: yup.array().of(yup.string()),
            note: yup.string().nullable(),
            mail: yup
                .string()
                .email(emailFormatMessage)
                .required(requiredMessage)
        }),
        enableReinitialize: true,
        onSubmit: async values => {
            try {
                const payload = {
                    contractDate: values?.contractDate
                        ? moment(values.contractDate).format("YYYY-MM-DD")
                        : undefined,
                    sector: values?.sector,
                    priority: Boolean(values?.priority),
                    category: values?.category,
                    // regionalDirectoriePlace: Zone'dan otomatik doldurulur, edit edilemez - payload'dan kaldırıldı
                    place: values?.place,
                    // regionalDirectorie: Zone'dan otomatik doldurulur, edit edilemez - payload'dan kaldırıldı
                    tp: values?.tp,
                    courierCount: values?.courierCount,
                    billingPeriod: values?.billingPeriod,
                    price: values?.price,
                    suretyAmountInclVAT: values?.suretyAmountInclVAT,
                    maturityDay: values?.maturityDay,
                    maxWorkingHour: values?.maxWorkingHour,
                    benefits: values?.benefits,
                    note: values?.note,
                    //priceAgreement: values?.priceAgreement,
                    mail: values?.mail
                };

                const result = await stores.companyStore.updateSubcompanyDetail({
                    ...payload,
                    subCompanyId: subcompanyId
                });

                setRecall(!recall);
                setMode(MODES.VIEW);
            } catch (error) {
                console.log(error);
                //Sentry.captureException(error);
            }
        }
    });

    const {values, errors, touched, isSubmitting, handleSubmit, setFieldValue} = formik;
    //#endregion
    const renderOn = useCallback(
        ({_mode = mode, view, edit}) => {
            if (!subcompanyDetail) return <></>;
            switch (_mode) {
                case MODES.VIEW:
                    return view;
                case MODES.EDIT:
                    return edit;
                default:
                    return view;
            }
        },
        [mode, subcompanyDetail]
    );

    return (
        <>
            <Form
                autoComplete="off"
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                onFinish={handleSubmit}
                data-testid="addSubCompanyForm"
            >
                <Descriptions
                    column={{xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1}}
                    title={
                        <>
                            <Row type="flex" justify="space-between">
                                Satış Bilgileri
                                {stores.meStore.checkRole("updateCompany") &&
                                    stores.meStore.checkPermission("subcompanies.edit") &&
                                    mode === MODES.VIEW && (
                                        <CustomButton
                                            type="primary"
                                            disabled={subCompany.data.disabled}
                                            icon={<EditOutlined />}
                                            onClick={handleOnChangeMode}
                                        />
                                    )}
                            </Row>
                        </>
                    }
                    bordered
                >
                    {/* Sütun 1: Sözleşme Tarihi, Çalışma Süresi, Satış Yapan, Öncelik, Bölge Müdürü, Fatura Dönemi, TP(Gerçekleşen), Teminat Tutarı, Fiyat Anlaşması, Yan Haklar */}
                    <Descriptions.Item span={1} label="Sözleşme Tarihi">
                        {renderOn({
                            view: values?.contractDate
                                ? moment(values.contractDate).format("DD.MM.YYYY")
                                : "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.contractDate && touched.contractDate
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.contractDate && touched.contractDate
                                            ? errors.contractDate
                                            : null
                                    }
                                >
                                    <DatePicker
                                        style={{width: "100%"}}
                                        format="DD.MM.YYYY"
                                        value={values?.contractDate}
                                        onChange={date => setFieldValue("contractDate", date)}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    {/* Sütun 2: Açılış Tarihi, Durum Kodu, Sektör, Kategori, Bölge Müdürlüğü, Fiyat Bölgesi, TP(Hedef), Ödeme Tutarı, Vade Günü, Kurye Sayısı, Max Çalışma Saati, Puantaj Yetkili Mail */}
                    <Descriptions.Item span={1} label="Açılış Tarihi">
                        {values?.openedDate ? moment(values.openedDate).format("DD.MM.YYYY") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Çalışma Süresi">
                        {subcompanyDetail?.workingMonths
                            ? `${subcompanyDetail?.workingMonths} Ay`
                            : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Durum Kodu">
                        {subCompanyDetailData?.statusCode || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Satışı Yapan">
                        {subCompanyDetailData?.sellerBy || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Sektör">
                        {renderOn({
                            view:
                                (Array.isArray(detailOptions?.sector) &&
                                    detailOptions.sector.find(
                                        item => item?.value === subcompanyDetail?.sector
                                    )?.name) ||
                                subcompanyDetail?.sector ||
                                "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.sector && touched.sector ? "error" : "success"
                                    }
                                    help={errors.sector && touched.sector ? errors.sector : null}
                                >
                                    <Select
                                        loading={detailOptionsLoading}
                                        style={{width: "100%"}}
                                        value={values?.sector}
                                        onChange={e => setFieldValue("sector", e)}
                                    >
                                        {Array.isArray(detailOptions?.sector) &&
                                            detailOptions.sector.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {item?.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Öncelik">
                        {renderOn({
                            view: values?.priority ? "Evet" : "Hayır",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.priority && touched.priority ? "error" : "success"
                                    }
                                    help={
                                        errors.priority && touched.priority ? errors.priority : null
                                    }
                                >
                                    <Select
                                        style={{width: "100%"}}
                                        value={values?.priority}
                                        onChange={e => setFieldValue("priority", e)}
                                    >
                                        <Select.Option value={true}>Evet</Select.Option>
                                        <Select.Option value={false}>Hayır</Select.Option>
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Kategori">
                        {renderOn({
                            view:
                                (Array.isArray(detailOptions?.category) &&
                                    detailOptions.category.find(
                                        item => item?.value === values?.category
                                    )?.name) ||
                                values?.category ||
                                "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.category && touched.category ? "error" : "success"
                                    }
                                    help={
                                        errors.category && touched.category ? errors.category : null
                                    }
                                >
                                    <Select
                                        loading={detailOptionsLoading}
                                        style={{width: "100%"}}
                                        value={values?.category}
                                        onChange={e => setFieldValue("category", e)}
                                    >
                                        {Array.isArray(detailOptions?.category) &&
                                            detailOptions.category.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {item?.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Bölge Müdürü">
                        {subcompanyDetail?.regionalDirectorie?.fullName ||
                            regionalDirectorie?.data?.fullName ||
                            "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Bölge Müdürlüğü">
                        {values?.regionalDirectoriePlace || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Fiyat Bölgesi">
                        {renderOn({
                            view: findInOptions({
                                group: "subCompanyDetailPlace",
                                keyName: "name",
                                value: findInOptions({
                                    group: "subCompanyDetailPlace",
                                    value: values?.place
                                })
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.place && touched.place ? "error" : "success"
                                    }
                                    help={errors.place && touched.place ? errors.place : null}
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.place}
                                        onChange={e => setFieldValue("place", e)}
                                    >
                                        {Array.isArray(options?.subCompanyDetailPlace) &&
                                            options.subCompanyDetailPlace.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {item?.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Fatura Dönemi">
                        {renderOn({
                            view: findInOptions({
                                group: "billingPeriod",
                                value: values?.billingPeriod
                            }),
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.billingPeriod && touched.billingPeriod
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.billingPeriod && touched.billingPeriod
                                            ? errors.billingPeriod
                                            : null
                                    }
                                >
                                    <Select
                                        loading={optionLoading}
                                        style={{width: "100%"}}
                                        value={values?.billingPeriod}
                                        onChange={e => setFieldValue("billingPeriod", e)}
                                    >
                                        {Array.isArray(options?.billingPeriod) &&
                                            options.billingPeriod.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {findInOptions({
                                                        group: "billingPeriod",
                                                        value: item?.value
                                                    })}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="TP (Hedef)">
                        {renderOn({
                            view: values?.tp || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={errors.tp && touched.tp ? "error" : "success"}
                                    help={errors.tp && touched.tp ? errors.tp : null}
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.tp}
                                        onChange={e => setFieldValue("tp", e)}
                                        min={0}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="TP (Gerçekleşen)">
                        {values?.tpActual || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Ödeme Tutarı (KDV Hariç)">
                        {renderOn({
                            view: values?.price || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.price && touched.price ? "error" : "success"
                                    }
                                    help={errors.price && touched.price ? errors.price : null}
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.price}
                                        onChange={e => setFieldValue("price", e)}
                                        min={0}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Teminat Tutarı (KDV Dahil)">
                        {renderOn({
                            view: values?.suretyAmountInclVAT || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.suretyAmountInclVAT && touched.suretyAmountInclVAT
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.suretyAmountInclVAT && touched.suretyAmountInclVAT
                                            ? errors.suretyAmountInclVAT
                                            : null
                                    }
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.suretyAmountInclVAT}
                                        onChange={e => setFieldValue("suretyAmountInclVAT", e)}
                                        min={0}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Vade Günü">
                        {renderOn({
                            view:
                                (Array.isArray(detailOptions?.maturityDay) &&
                                    detailOptions.maturityDay.find(
                                        item => item?.value === values?.maturityDay
                                    )?.name) ||
                                findInOptions({
                                    group: "maturityDay",
                                    value: values?.maturityDay
                                }) ||
                                "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.maturityDay && touched.maturityDay
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.maturityDay && touched.maturityDay
                                            ? errors.maturityDay
                                            : null
                                    }
                                >
                                    <Select
                                        loading={detailOptionsLoading}
                                        style={{width: "100%"}}
                                        value={values?.maturityDay}
                                        onChange={e => setFieldValue("maturityDay", e)}
                                    >
                                        {Array.isArray(detailOptions?.maturityDay) &&
                                            detailOptions.maturityDay.map(item => (
                                                <Select.Option key={item?.key} value={item?.value}>
                                                    {item?.name}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Fiyat Anlaşması">
                        {renderOn({
                            view:
                                findInOptions({
                                    group: "priceAgreement",
                                    value: values?.priceAgreement
                                }) || "-"
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Kurye Sayısı">
                        {renderOn({
                            view: values?.courierCount || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.courierCount && touched.courierCount
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.courierCount && touched.courierCount
                                            ? errors.courierCount
                                            : null
                                    }
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.courierCount}
                                        onChange={e => setFieldValue("courierCount", e)}
                                        min={0}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Max Çalışma Saati">
                        {renderOn({
                            view:
                                values?.maxWorkingHour !== undefined &&
                                values?.maxWorkingHour !== null
                                    ? values.maxWorkingHour
                                    : "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.maxWorkingHour && touched.maxWorkingHour
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        errors.maxWorkingHour && touched.maxWorkingHour
                                            ? errors.maxWorkingHour
                                            : null
                                    }
                                >
                                    <InputNumber
                                        style={{width: "100%"}}
                                        value={values?.maxWorkingHour}
                                        onChange={e => setFieldValue("maxWorkingHour", e)}
                                        min={0}
                                        max={24}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Yan Haklar">
                        {renderOn({
                            view:
                                values?.benefits && values.benefits.length > 0
                                    ? values.benefits
                                          .map(benefitValue => {
                                              const benefitOption = Array.isArray(
                                                  detailOptions?.benefits?.values
                                              )
                                                  ? detailOptions.benefits.values.find(
                                                        item => item?.value === benefitValue
                                                    )
                                                  : null;
                                              return benefitOption?.name || benefitValue;
                                          })
                                          .join(", ")
                                    : "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.benefits && touched.benefits ? "error" : "success"
                                    }
                                    help={
                                        errors.benefits && touched.benefits ? errors.benefits : null
                                    }
                                >
                                    <Select
                                        mode="multiple"
                                        loading={detailOptionsLoading}
                                        style={{width: "100%"}}
                                        value={values?.benefits || []}
                                        placeholder={
                                            detailOptionsLoading
                                                ? "Yükleniyor..."
                                                : Array.isArray(detailOptions?.benefits?.values) &&
                                                  detailOptions.benefits.values.length > 0
                                                ? "Seçiniz"
                                                : "Seçenekler yüklenemedi"
                                        }
                                        notFoundContent={
                                            detailOptionsLoading
                                                ? "Yükleniyor..."
                                                : Array.isArray(detailOptions?.benefits?.values) &&
                                                  detailOptions.benefits.values.length === 0
                                                ? "Seçenek bulunamadı"
                                                : "Seçenekler yüklenemedi"
                                        }
                                        onChange={selected => {
                                            const groups = detailOptions?.benefits?.groups || [];
                                            // Eğer bir gruptan seçim yapıldıysa, diğer gruplardan seçimleri kaldır
                                            const findGroupIndex = val =>
                                                groups.findIndex(g => g.includes(val));
                                            if (!Array.isArray(selected)) return;
                                            if (selected.length === 0)
                                                return setFieldValue("benefits", []);
                                            const selectedGroups = new Set(
                                                selected
                                                    .map(v => findGroupIndex(v))
                                                    .filter(i => i >= 0)
                                            );
                                            // Birden fazla grup seçildiyse, ilk seçilen grubun dışındakileri at
                                            const primaryGroupIndex = [...selectedGroups][0];
                                            const filtered = selected.filter(
                                                v => findGroupIndex(v) === primaryGroupIndex
                                            );
                                            setFieldValue("benefits", filtered);
                                        }}
                                    >
                                        {Array.isArray(detailOptions?.benefits?.values) &&
                                            detailOptions.benefits.values.length > 0 &&
                                            detailOptions.benefits.values.map(item => {
                                                const groups =
                                                    detailOptions?.benefits?.groups || [];
                                                const findGroupIndex = val =>
                                                    groups.findIndex(g => g.includes(val));
                                                const selectedGroupIndex =
                                                    (values?.benefits?.[0] &&
                                                        findGroupIndex(values?.benefits?.[0])) ??
                                                    -1;
                                                const optionGroupIndex = findGroupIndex(
                                                    item?.value
                                                );
                                                const disabled =
                                                    selectedGroupIndex >= 0 &&
                                                    optionGroupIndex >= 0 &&
                                                    selectedGroupIndex !== optionGroupIndex;
                                                return (
                                                    <Select.Option
                                                        key={item?.key}
                                                        value={item?.value}
                                                        disabled={disabled}
                                                    >
                                                        {item?.name}
                                                    </Select.Option>
                                                );
                                            })}
                                    </Select>
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label="Puantaj Yetkilisi Mail">
                        {renderOn({
                            view: values?.mail || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.mail && touched.mail ? "error" : "success"
                                    }
                                    help={errors.mail && touched.mail ? errors.mail : null}
                                >
                                    <Input
                                        type="email"
                                        style={{width: "100%"}}
                                        value={values?.mail}
                                        onChange={e => setFieldValue("mail", e.target.value)}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                    <Descriptions.Item span={2} label="Not">
                        {renderOn({
                            view: values?.note || "-",
                            edit: (
                                <Form.Item
                                    style={{marginBottom: "0px"}}
                                    validateStatus={
                                        errors.note && touched.note ? "error" : "success"
                                    }
                                    help={errors.note && touched.note ? errors.note : null}
                                >
                                    <Input
                                        style={{width: "100%"}}
                                        value={values?.note}
                                        onChange={e => setFieldValue("note", e.target.value)}
                                    />
                                </Form.Item>
                            )
                        })}
                    </Descriptions.Item>
                </Descriptions>
                {mode === MODES.EDIT && (
                    <Row type="flex" justify={"end"} style={{gap: "8px"}}>
                        <CustomButton onClick={handleOnChangeMode}>
                            {t("modal.cancelButton", {ns: "common"})}
                        </CustomButton>
                        <CustomButton type="primary" htmlType="submit" disabled={isSubmitting}>
                            {t("actions.update", {ns: "common"})}
                        </CustomButton>
                    </Row>
                )}
            </Form>
            <Descriptions
                column={{xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1}}
                title={
                    <>
                        <Row type="flex" justify="space-between">
                            Kayıp Bilgileri
                        </Row>
                    </>
                }
                style={{marginTop: "30px"}}
                bordered
            >
                <Descriptions.Item span={1} label="Kayıp Ayı">
                    {subcompanyDetail?.lostMonth
                        ? moment(subcompanyDetail?.lostMonth, "M").format("MMMM")
                        : "-"}
                </Descriptions.Item>
                <Descriptions.Item span={1} label="Kayıp Tarihi">
                    {subcompanyDetail?.lostDate
                        ? moment(subcompanyDetail?.lostDate)?.format("DD.MM.YYYY")
                        : "-"}
                </Descriptions.Item>
                <Descriptions.Item span={1} label="Kayıp Nedeni">
                    {findInOptions({
                        group: "lostReason",
                        value: subcompanyDetail?.lostSubject
                    }) || "-"}
                </Descriptions.Item>
                <Descriptions.Item span={1} label="Kime Kaybedildi">
                    {subcompanyDetail?.lostedBy || "-"}
                </Descriptions.Item>
            </Descriptions>
        </>
    );
};
